<template>
	<div class="position-relative login-bg-root">
		<!-- Animated background (ported from LandingStrasbourg) -->
		<div class="bg-gradient"></div>
		<div class="bg-orb bg-orb-1"></div>
		<div class="bg-orb bg-orb-2"></div>
		<div class="bg-orb bg-orb-3"></div>
		<div class="bg-grid"></div>
		<img class="bg-plane" src="@/assets/icon-plane.png" alt="" aria-hidden="true" />

		<div class="authentication-wrapper authentication-basic container-p-y p-4 p-sm-0">
			<div class="authentication-inner py-6">
				<!-- Register Card -->
				<div class="card p-md-7 p-1">
					<!-- Logo -->
					<div class="app-brand justify-content-center mt-5">
						<a href="javascript:void(0)" class="app-brand-link gap-2">
							<span class="app-brand-logo demo">
								<span style="color: var(--bs-primary)">
									<img :src="logoUrl" :style="logoSize()" alt="Logo">
								</span>
							</span>
						</a>
					</div>
					<!-- /Logo -->
					<div class="card-body mt-1">
						<p class="mb-5 text-center">{{ $t('auth.register.chooseType') }}</p>

						<div class="d-flex gap-3 mb-5">
							<div
								class="card flex-fill text-center p-3 cursor-pointer border-2"
								:class="form.accountType === 'individual' ? 'border-primary shadow-sm' : ''"
								@click="form.accountType = 'individual'"
								style="cursor: pointer;">
								<i class="ri-user-line ri-24px mb-1" :class="form.accountType === 'individual' ? 'text-primary' : ''"></i>
								<span :class="form.accountType === 'individual' ? 'text-primary fw-semibold' : ''">{{ $t('auth.register.individual') }}</span>
							</div>
							<div
								class="card flex-fill text-center p-3 cursor-pointer border-2"
								:class="form.accountType === 'company' ? 'border-primary shadow-sm' : ''"
								@click="form.accountType = 'company'"
								style="cursor: pointer;">
								<i class="ri-building-line ri-24px mb-1" :class="form.accountType === 'company' ? 'text-primary' : ''"></i>
								<span :class="form.accountType === 'company' ? 'text-primary fw-semibold' : ''">{{ $t('auth.register.company') }}</span>
							</div>
						</div>

						<transition name="slide-down" @enter="slideEnter" @leave="slideLeave">
						<form v-if="form.accountType" id="formAuthentication" @submit.prevent="registerUser" class="mb-5">
							<div class="form-floating form-floating-outline mb-5">
								<input
									type="text"
									class="form-control"
									id="firstName"
									name="firstName"
									:placeholder="$t('auth.register.firstNamePlaceholder')"
									v-model="form.firstName"
									@input="clearError('firstName')"
									autofocus />
								<label for="firstName">{{ $t('common.fields.firstName') }}</label>
								<small v-if="errors.firstName" class="text-danger">{{ errors.firstName }}</small>
							</div>
							<div class="form-floating form-floating-outline mb-5">
								<input
									type="text"
									class="form-control"
									id="lastName"
									name="lastName"
									:placeholder="$t('auth.register.lastNamePlaceholder')"
									v-model="form.lastName"
									@input="clearError('lastName')"
									autofocus />
								<label for="lastName">{{ $t('common.fields.lastName') }}</label>
								<small v-if="errors.lastName" class="text-danger">{{ errors.lastName }}</small>
							</div>
							<div class="form-floating form-floating-outline mb-5">
								<input 
									type="text"
									id="bs-datepicker-basic"
									placeholder="DD/MM/YYYY"
									class="form-control"
									ref="birthDate"
									@input="handleDateInput"
									v-model="form.birthDate"
								/>
								<label for="birthDate">{{ $t('common.fields.birthDate') }}</label>
								<small v-if="errors.birthDate" class="text-danger">{{ errors.birthDate }}</small>
							</div>
							<div class="form-floating form-floating-outline mb-5">
								<input type="text" class="form-control" id="email" name="email" :placeholder="$t('auth.login.emailPlaceholder')" v-model="form.email" @input="clearError('email')" />
								<label for="email">{{ $t('common.fields.email') }}</label>
								<small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
							</div>
							<div class="mb-5 form-password-toggle">
								<div class="input-group input-group-merge">
									<div class="form-floating form-floating-outline">
										<input
											:type="showPassword ? 'text' : 'password'"
											id="password"
											class="form-control"
											name="password"
											placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
											aria-describedby="password" v-model="form.password" @input="clearError('password')" />
										<label for="password">{{ $t('common.fields.password') }}</label>
									</div>
									<span class="input-group-text cursor-pointer" @click="showPassword = !showPassword">
										<i :class="showPassword ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
									</span>
									<small v-if="errors.password" class="text-danger">{{ errors.password }}</small>
								</div>
							</div>
							<div class="form-floating form-floating-outline mb-5" v-if="form.accountType === 'company'">
								<input
									type="text"
									class="form-control"
									id="companyName"
									name="companyName"
									:placeholder="$t('auth.register.companyPlaceholder')"
									v-model="form.companyName"
									@input="clearError('companyName')"
									autofocus />
								<label for="companyName">{{ $t('common.fields.companyName') }}</label>
								<small v-if="errors.companyName" class="text-danger">{{ errors.companyName }}</small>
							</div>

							<div class="mb-5">
								<div class="form-check mt-2">
									<input class="form-check-input" type="checkbox" id="terms-conditions" name="terms" v-model="form.terms" />
									<label class="form-check-label" for="terms-conditions">
										{{ $t('auth.register.terms') }}
										<a href="https://pika-aero.com/cgv" target="_blank">{{ $t('auth.register.termsLink') }}</a>
									</label>
								</div>
								<small v-if="errors.terms" class="text-danger">{{ errors.terms }}</small>
							</div>
							<button class="btn btn-primary d-grid w-100">{{ $t('auth.register.submit') }}</button>
						</form>
						</transition>

						<p class="text-center" v-if="form.accountType">
							<span>{{ $t('auth.register.hasAccount') }}</span>
							<router-link to="/login">
								<span>{{ $t('auth.register.loginLink') }}</span>
							</router-link>
						</p>
					</div>
				</div>
				<!-- Register Card -->
			</div>
		</div>
	</div>
</template>

<script>
import { event } from "vue-gtag"
import "@/assets/vendor/css/pages/page-auth.css";

import HTTP from "@/axios";

export default {
	name: "RegisterPage",

	data() {
		return {
			showPassword: false,
			domain: null,
			hideReferral: false,
			form: {
				firstName: "",
				lastName: "",
				email: "",
				password: "",
				companyName: "",
				accountType: null,
				birthDate: null,
				terms: false,
				referralCode: null
			},
			errors: {},
		};
	},

	async mounted() {
		this.domain = window.location.hostname;

		if (this.$route.query.r) {
			this.form.referralCode = this.$route.query.r;
		}

		if (this.domain === "2mtraining.pika-aero.com") {
			this.form.referralCode = "2MTraining";
		}

		if (this.domain === "seris.pika-aero.com") {
			this.form.referralCode = "Seris";
		}

		if (this.domain === "excellence-academy.pika-aero.com") {
			this.form.referralCode = "Excellence Academy";
		}

		if (this.domain === "strasbourg.pika-aero.com") {
			this.form.referralCode = "Strasbourg Airport";
		}
	},

	computed: {
		logoUrl() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return require('@/assets/logo-exl.png');
			}
			else if (this.domain === "seris.pika-aero.com") {
				return require('@/assets/logo-seris.svg');
			}
			else if (this.domain === "vinci.pika-aero.com") {
				return require('@/assets/logo-vinci.png');
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return require('@/assets/logo-strasbourg.png');
			} else {
				return require('@/assets/logo.png');
			}
		},

		brandName() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return "Excellence Academy";
			}
			else if (this.domain === "seris.pika-aero.com") {
				return "Seris Academy";
			}
			else if (this.domain === "vinci.pika-aero.com") {
				return "Vinci Airport";
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return "Aéroport de Strasbourg";
			} else {
				return "Pika Aero";
			}
		}
	},

	methods: {
		slideEnter(el, done) {
			el.style.overflow = 'hidden';
			el.style.maxHeight = '0';
			el.style.opacity = '0';
			requestAnimationFrame(() => {
				el.style.transition = 'max-height 0.4s ease, opacity 0.4s ease';
				el.style.maxHeight = el.scrollHeight + 'px';
				el.style.opacity = '1';
				el.addEventListener('transitionend', () => {
					el.style.maxHeight = '';
					el.style.overflow = '';
					done();
				}, { once: true });
			});
		},

		slideLeave(el, done) {
			el.style.overflow = 'hidden';
			el.style.maxHeight = el.scrollHeight + 'px';
			el.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
			requestAnimationFrame(() => {
				el.style.maxHeight = '0';
				el.style.opacity = '0';
				el.addEventListener('transitionend', done, { once: true });
			});
		},

		handleDateInput(event) {
			let input = event.target.value;

			// Enlever tous les caractères non numériques
			input = input.replace(/\D/g, '');

			// Formatage automatique en DD/MM/YYYY
			if (input.length <= 2) {
				this.form.birthDate = input;
			} else if (input.length <= 4) {
				this.form.birthDate = `${input.slice(0, 2)}/${input.slice(2)}`;
			} else {
				this.form.birthDate = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
			}
		},

		logoSize() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return { width: "120px" };
			}
			else if (this.domain === "seris.pika-aero.com") {
				return { width: "100px" };
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return { width: "180px" };
			} else {
				return { width: "160px" };
			}
		},

		clearError(field) {
			// Supprime l'erreur pour le champ spécifié
			if (this.errors[field]) {
				delete this.errors[field];
			}
		},

		validateForm() {
			this.errors = {};

			if (!this.form.firstName) {
				this.errors.firstName = this.$t('validation.requiredFirstName');
			}

			if (!this.form.lastName) {
				this.errors.lastName = this.$t('validation.requiredLastName');
			}

			if (!this.form.birthDate) {
				this.errors.birthDate = this.$t('validation.invalidBirthDate');
			}

			if (!this.form.birthDate || !this.isValidDate(this.form.birthDate)) {
				this.errors.birthDate = this.$t('validation.invalidBirthDate');
			}

			if (!this.form.email || !this.validateEmail(this.form.email)) {
				this.errors.email = this.$t('validation.invalidEmail');
			}

			// Vérification du mot de passe
			if (!this.form.password) {
				this.errors.password = this.$t('validation.requiredPassword');
			} else {
				const password = this.form.password;

				// 12 caractères minimum
				if (password.length < 12) {
					this.errors.password = this.$t('validation.passwordMinLength');
				}
			}

			if (!this.form.terms) {
				this.errors.terms = this.$t('validation.requiredTerms');
			}

			if (this.form.accountType === 'company' && !this.form.companyName) {
				this.errors.companyName = this.$t('validation.requiredCompanyName');
			}

			return Object.keys(this.errors).length === 0;
		},

		validateEmail(email) {
			const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return re.test(email);
		},

		isValidDate(str) {
			if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str))
				return false;

			const [dd, mm, yyyy] = str.split('/').map(Number);

			const date = new Date(yyyy, mm - 1, dd);
			// construire une date ISO "YYYY-MM-DD"
			// const date = new Date(`${yyyy}-${mm.toString().padStart(2,'0')}-${dd.toString().padStart(2,'0')}`);
			// vérifier que la date existe vraiment
			return (
				date.getFullYear() === yyyy &&
				date.getMonth() + 1   === mm &&
				date.getDate()        === dd
			);
		},

		async registerUser() {
			const validated = this.validateForm();

			if (!validated)
				return;

			let data = { ...this.form };
			delete data.terms;
			delete data.accountType;

			if (this.form.accountType === 'individual') {
				data.companyName = "PARTICULIER-" + this.form.lastName.toUpperCase();
			}

			try {
				const response = await HTTP.post('/auth/registration', data);

				// Save JWT
				this.$store.dispatch("saveJwt", response.data.accessToken);

				event("register", {
					'send_to': 'AW-16956522772/v7O2CLfs47IaEJSCwJU_'
				});

				if (this.form.accountType === 'individual') {
					this.$router.push("/");
				} else {
					this.$router.push("/setup-company");
				}
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		}
	}
};
</script>

<style scoped>
.login-bg-root {
	min-height: 100vh;
}

.login-bg-root .authentication-wrapper {
	position: relative;
	z-index: 3;
}

.login-bg-root > .card,
.login-bg-root .authentication-inner > .card {
	background-color: rgba(255, 255, 255, 0.85);
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	border: 1px solid rgba(255, 255, 255, 0.6);
	box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08);
}

/* Animated background */
.bg-gradient {
	position: fixed;
	inset: 0;
	background: linear-gradient(135deg, #eef2ff 0%, #faf5ff 40%, #fef3f2 100%);
	z-index: 0;
}

.bg-orb {
	position: fixed;
	border-radius: 50%;
	filter: blur(80px);
	opacity: 0.5;
	z-index: 1;
	pointer-events: none;
}

.bg-orb-1 {
	width: 500px;
	height: 500px;
	background: radial-gradient(circle, #818cf8 0%, transparent 70%);
	top: -150px;
	left: -100px;
	animation: float-1 18s ease-in-out infinite;
}

.bg-orb-2 {
	width: 600px;
	height: 600px;
	background: radial-gradient(circle, #c084fc 0%, transparent 70%);
	bottom: -200px;
	right: -150px;
	animation: float-2 22s ease-in-out infinite;
}

.bg-orb-3 {
	width: 400px;
	height: 400px;
	background: radial-gradient(circle, #38bdf8 0%, transparent 70%);
	top: 40%;
	left: 50%;
	transform: translateX(-50%);
	opacity: 0.3;
	animation: float-3 15s ease-in-out infinite;
}

@keyframes float-1 {
	0%, 100% { transform: translate(0, 0) scale(1); }
	50% { transform: translate(60px, 80px) scale(1.1); }
}

@keyframes float-2 {
	0%, 100% { transform: translate(0, 0) scale(1); }
	50% { transform: translate(-80px, -60px) scale(1.15); }
}

@keyframes float-3 {
	0%, 100% { transform: translate(-50%, 0) scale(1); }
	50% { transform: translate(-50%, -40px) scale(1.08); }
}

.bg-grid {
	position: fixed;
	inset: 0;
	background-image:
		linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
		linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px);
	background-size: 60px 60px;
	mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
	-webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
	z-index: 2;
	pointer-events: none;
}

.bg-plane {
	position: fixed;
	width: 168px;
	height: auto;
	top: 15%;
	right: 8%;
	z-index: 2;
	pointer-events: none;
	user-select: none;
	animation: plane-drift 12s ease-in-out infinite;
}

@keyframes plane-drift {
	0%, 100% { transform: translate(0, 0); }
	50% { transform: translate(20px, -15px); }
}
</style>
